import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminApi, formatApiError } from "../auth";
import { ChevronLeft, Plus, Minus, XCircle } from "lucide-react";

const AdminStockDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState(1);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");

  const load = () => adminApi.get(`/stock/${id}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const adjust = async (delta) => {
    setErr("");
    try {
      await adminApi.post(`/stock/${id}/adjust`, { change_amount: delta, reason });
      setReason("");
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };
  const markOOS = async () => {
    setErr("");
    if (!window.confirm("Mark product as out of stock?")) return;
    try { await adminApi.post(`/stock/${id}/out-of-stock`); load(); } catch (e) { setErr(formatApiError(e)); }
  };

  if (!data) return <p className="vd-muted">Loading…</p>;

  return (
    <div data-testid="admin-stock-detail-page">
      <Link to="/admin/stock" className="inline-flex items-center gap-1 vd-muted text-sm mb-3" data-testid="stock-back-link"><ChevronLeft size={14} /> Back to stock</Link>
      <h1 style={{ fontSize: 28 }}>{data.brand} {data.model}</h1>
      <p className="vd-muted mb-6">{data.size}</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="vd-card p-6" data-testid="stock-adjust-card">
          <p className="vd-muted text-xs uppercase tracking-wider">Current stock</p>
          <p className="vd-heading font-extrabold" style={{ fontSize: 48, color: data.stock_qty < 5 ? "var(--primary-red)" : "var(--text-heading)" }} data-testid="stock-current-qty">{data.stock_qty}</p>

          <div className="mt-5">
            <label className="vd-muted text-xs uppercase tracking-wider">Adjustment amount</label>
            <input type="number" min="1" className="vd-input mt-1" value={amount} onChange={(e) => setAmount(Number(e.target.value) || 1)} data-testid="stock-adjust-amount" />
            <label className="vd-muted text-xs uppercase tracking-wider mt-3 inline-block">Reason (optional)</label>
            <input className="vd-input mt-1" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. New shipment received" data-testid="stock-adjust-reason" />
          </div>

          {err && <p className="text-sm mt-3" style={{ color: "var(--primary-red)" }} data-testid="stock-error">{err}</p>}

          <div className="flex gap-2 mt-5">
            <button onClick={() => adjust(amount)} className="vd-btn-primary justify-center" data-testid="stock-add-btn"><Plus size={14} /> Increase</button>
            <button onClick={() => adjust(-amount)} className="vd-btn-secondary justify-center" data-testid="stock-decrease-btn"><Minus size={14} /> Decrease</button>
            <button onClick={markOOS} className="vd-btn-secondary justify-center" style={{ color: "var(--primary-red)", borderColor: "var(--primary-red)" }} data-testid="stock-oos-btn"><XCircle size={14} /> Out of stock</button>
          </div>
        </div>

        <div className="vd-card p-6" data-testid="stock-history-card">
          <p className="vd-heading font-semibold mb-3">Stock history</p>
          {data.history.length === 0 ? <p className="vd-muted text-sm">No changes yet.</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left vd-muted uppercase text-xs tracking-wider">
                <th className="py-2">When</th><th>Change</th><th>Prev → New</th><th>By</th>
              </tr></thead>
              <tbody>
                {data.history.map((h) => (
                  <tr key={h.id} className="border-t" style={{ borderColor: "var(--border-color)" }} data-testid={`stock-history-row-${h.id}`}>
                    <td className="py-2 vd-muted text-xs">{new Date(h.timestamp).toLocaleString()}</td>
                    <td style={{ color: h.change_amount >= 0 ? "#00A35C" : "var(--primary-red)" }} className="font-semibold">{h.change_amount >= 0 ? "+" : ""}{h.change_amount}</td>
                    <td className="vd-muted">{h.previous_qty} → {h.new_qty}</td>
                    <td className="vd-muted text-xs">{h.admin_user}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminStockDetail;
